import { t } from '../../i18n/init.js';
import type { TourSnapshot } from '../../tour/types.js';
import type { ComponentInstance } from '../component.js';
import { setText } from '../dom-helpers.js';

export interface TourStatusProps {
  tourSnapshot: TourSnapshot;
}

// Top-center pill that announces tour progress for assistive tech.
// `role="status"` + `aria-live="polite"` ensures screen readers pick up the
// step change without interrupting the user.
export function mountTourStatus(
  root: HTMLElement,
  initial: TourStatusProps,
): ComponentInstance<TourStatusProps> {
  const wrap = document.createElement('div');
  wrap.classList.add('viewer-ui-interactive');
  wrap.setAttribute('role', 'status');
  wrap.setAttribute('aria-live', 'polite');
  wrap.setAttribute('aria-atomic', 'true');
  wrap.style.cssText =
    'position:fixed;top:1rem;left:50%;transform:translateX(-50%);background:rgba(15,15,17,0.85);backdrop-filter:blur(8px);border:1px solid rgba(168,85,247,0.30);padding:0.375rem 0.875rem;border-radius:9999px;display:none;align-items:center;gap:0.5rem;font-family:system-ui,sans-serif;font-size:0.6875rem;color:#e9d5ff;z-index:11;pointer-events:none';

  const dot = document.createElement('span');
  dot.setAttribute('aria-hidden', 'true');
  dot.style.cssText =
    'display:inline-block;width:0.375rem;height:0.375rem;border-radius:9999px;background:#c084fc';
  wrap.appendChild(dot);

  const tag = document.createElement('span');
  tag.style.cssText =
    'text-transform:uppercase;letter-spacing:0.08em;color:#d8b4fe;font-family:ui-monospace,SFMono-Regular,Menlo,monospace';
  tag.textContent = t('viewer.tour.tag');
  wrap.appendChild(tag);

  const sep1 = document.createElement('span');
  sep1.setAttribute('aria-hidden', 'true');
  sep1.style.cssText = 'color:#71717a';
  sep1.textContent = '·';
  wrap.appendChild(sep1);

  const label = document.createElement('span');
  label.style.cssText = 'color:#fafafa;font-weight:600';
  wrap.appendChild(label);

  const counter = document.createElement('span');
  counter.style.cssText = 'color:#a1a1aa;font-family:ui-monospace,SFMono-Regular,Menlo,monospace';
  wrap.appendChild(counter);

  root.appendChild(wrap);

  const apply = (snap: TourSnapshot): void => {
    if (snap.kind === 'idle') {
      wrap.style.display = 'none';
      return;
    }
    wrap.style.display = 'flex';
    setText(label, snap.label);
    setText(counter, `${snap.index + 1} / ${snap.total}`);
  };

  apply(initial.tourSnapshot);

  return {
    update(props: TourStatusProps) {
      apply(props.tourSnapshot);
    },
    cleanup() {
      wrap.remove();
    },
  };
}
